/**
 * The January ask letters (Sponsors Part 3, sp05 "The letter we send"): one letter page per ask,
 * on letterhead, from the sponsorship lead. Last year's level is named, never an amount; a
 * family who said they would sponsor is named in its own line.
 */
import type { ReactNode } from 'react';
import { Paper, PrintRoot, formatDate } from '@cider/ui';
import { useLoad } from '../views/admin/shared.js';
import { usePhase } from '../shell/phase.js';

interface Letter {
  askId: string;
  organizationName: string;
  contactName: string | null;
  address: string | null;
  lastLevel: string | null;
  lastYear: number | null;
  familyLine: string | null;
  says: string;
}

export function AskLetters({ year }: { year: string }): ReactNode {
  const ids = new URLSearchParams(window.location.search).get('asks') ?? '';
  const letters = useLoad<Letter[]>(
    `/marketing/asks/letters?year=${encodeURIComponent(year)}&ids=${encodeURIComponent(ids)}`,
  );
  // The signup page's host, from SITE_ORIGIN — the app prints no host of its own.
  const { siteOrigin } = usePhase();
  if (letters.error) return <p role="alert">{letters.error}</p>;
  const rows = letters.data;
  if (!rows || siteOrigin === null) return <p>Writing the letters…</p>;
  const site = new URL(siteOrigin).host;
  const today = new Date().toISOString();
  return (
    <PrintRoot
      kind="letter"
      title={`Ask letters ${year}`}
      pages={rows.map((l) => (
        <Paper
          key={l.askId}
          masthead="Saint Lawrence Apple Fest"
          submast="Saint Lawrence Parish · Utica, Michigan · sponsors@applefestmi.com"
          stamp={formatDate(today, { year: true }).toUpperCase()}
        >
          <div className="paper-letter">
            <p className="ask-letter__to">
              {l.organizationName}
              {l.contactName ? (
                <>
                  <br />
                  {`Attention: ${l.contactName}`}
                </>
              ) : null}
              {l.address ? (
                <>
                  <br />
                  {l.address}
                </>
              ) : null}
            </p>
            <p>Dear {l.contactName ?? 'friends'},</p>
            <p>{l.says}</p>
            <p>
              Apple Fest is the weekend the parish and the school put on together, and everything
              raised goes to the school, the parish and athletics. Our sponsors are named on the
              wall, on the signage and in the program, all weekend.
            </p>
            {l.familyLine ? <p>{l.familyLine}</p> : null}
            <p>
              Say yes at <b>{site}/sponsor/signup</b>, or write back to this letter and we will take
              care of the rest.
            </p>
            <p className="ask-letter__sign">
              With thanks,
              <br />
              <br />
              Sponsorships
              <br />
              Saint Lawrence Apple Fest
            </p>
          </div>
        </Paper>
      ))}
    />
  );
}
